/**
 * How Sight Reading engraves its music with OSMD: the line gap, the line weights, the staff
 * distances and the note spacing. There is one notation look across the learning surfaces, so
 * every OSMD play surface (Sight Reading's OSMD scene, Repertoire, exercise runs) engraves with
 * these rules; a change here changes them all. The sizes are in OSMD units of 10 px.
 *
 * Sight Reading's own staff renderer draws with the same proportions: staff lines a tenth of a
 * line gap thick (`StaffLine` `DefaultConfig`: 2 px lines 20 px apart).
 */
import type { EngravingRules } from '@museflow-ai/opensheetmusicdisplay';

/** The engraving rules Sight Reading sets, by their OSMD names. */
export type SightReadingEngravingRules = Pick<
  EngravingRules,
  | 'DefaultVexFlowNoteFont'
  | 'StaffHeight'
  | 'BetweenStaffDistance'
  | 'StaffDistance'
  | 'BetweenStaffLinesDistance'
  | 'StaffLineWidth'
  | 'StemWidth'
  | 'LedgerLineWidth'
  | 'WedgeLineWidth'
  | 'SystemLeftMargin'
  | 'SystemRightMargin'
  | 'PageLeftMargin'
  | 'PageRightMargin'
  | 'PageTopMargin'
  | 'PageBottomMargin'
  | 'MeasureLeftMargin'
  | 'MeasureRightMargin'
  | 'VoiceSpacingMultiplierVexflow'
  | 'VoiceSpacingAddendVexflow'
  | 'SheetMaximumWidth'
  | 'NewSystemAtXMLNewSystemAttribute'
>;

export const SIGHT_READING_ENGRAVING_RULES: Readonly<SightReadingEngravingRules> = {
  DefaultVexFlowNoteFont: 'gonville',
  StaffHeight: 6.0,
  // Treble and bass staves close together.
  BetweenStaffDistance: 3.0,
  StaffDistance: 4.0,
  BetweenStaffLinesDistance: 1.0,
  StaffLineWidth: 0.1,
  StemWidth: 0.12,
  LedgerLineWidth: 1.0,
  WedgeLineWidth: 0.1,
  SystemLeftMargin: 0,
  SystemRightMargin: 0,
  PageLeftMargin: 1,
  PageRightMargin: 1,
  PageTopMargin: 1,
  PageBottomMargin: 1,
  MeasureLeftMargin: 0.2,
  MeasureRightMargin: 0.2,
  // Compact horizontal note spacing.
  VoiceSpacingMultiplierVexflow: 0.45,
  VoiceSpacingAddendVexflow: 1.5,
  SheetMaximumWidth: 32767,
  NewSystemAtXMLNewSystemAttribute: true,
};
